import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

function PasswordField({ id, testid, label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} data-testid={testid} value={value} onChange={onChange} className="mt-1.5 bg-white pr-11" autoComplete="new-password" />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 transition-colors hover:text-forest"
        >
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (form.next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed — use it from your next login");
      setOpen(false);
      setForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e, "Could not change password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="change-password-button" className="rounded-full border-forest/20 font-display text-forest hover:bg-forest hover:text-cream">
          <KeyRound className="mr-2 h-4 w-4" /> Password
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-cream sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-forest-deep">Change Admin Password</DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <PasswordField id="cp-current" testid="cp-current-input" label="Current Password" value={form.current} onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))} />
          <PasswordField id="cp-next" testid="cp-next-input" label="New Password" value={form.next} onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))} />
          <PasswordField id="cp-confirm" testid="cp-confirm-input" label="Confirm New Password" value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} />
          <Button onClick={submit} disabled={saving} data-testid="cp-save-button" className="w-full rounded-full bg-forest py-5 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
            {saving ? "Saving..." : "Change Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
